import { create } from 'zustand';
import { authStorage, type AuthPersistence } from '@core/auth/authStorage';
import { isTokenExpired } from '@core/auth/jwt';
import { usePermissionStore } from './permissions.store';
import type { User } from '@core/types/auth';

/**
 * Auth store — the state half of `core/services/auth.service.ts`.
 *
 * The HTTP half lives in `core/api/auth.api.ts`. That split is the point: the
 * Angular service called `Router.navigateByUrl` and `MatSnackBar.open` from inside
 * its `login()` tap, wrapped in `setTimeout(..., 500)` because the toast and the
 * navigation raced each other. Navigation and toasts are now the page's job, so the
 * timeout disappears entirely.
 *
 * `isAuthenticated()` is called synchronously by the route guard, so this is a
 * store rather than a query.
 */
interface AuthState {
  token: string | null;
  user: User | null;
  /** Re-entrancy guard, ported from `AuthService.isLoggingOut`. */
  isLoggingOut: boolean;

  setSession: (token: string, user: User, persistence: AuthPersistence) => void;
  setUser: (user: User) => void;
  /** Overwrite just the token — used by the OTP flow. */
  setToken: (token: string) => void;
  /** Clear local state with no API call. Used by the 401 path and token expiry. */
  localLogout: () => void;
  isAuthenticated: () => boolean;
}

/**
 * Hydrate from storage at module load, so the first render already knows whether the
 * user is signed in. Mirrors `AuthService.initializeUser()`, including its rule that
 * a stored-but-expired token is discarded immediately.
 */
function initialState(): Pick<AuthState, 'token' | 'user'> {
  const token = authStorage.getToken();
  const user = authStorage.getUser();

  if (token && isTokenExpired(token)) {
    authStorage.clear();
    return { token: null, user: null };
  }
  return { token, user };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...initialState(),
  isLoggingOut: false,

  setSession: (token, user, persistence) => {
    authStorage.setSession(token, user, persistence);
    set({ token, user, isLoggingOut: false });
  },

  setUser: (user) => {
    authStorage.setUser(user);
    set({ user });
  },

  setToken: (token) => {
    authStorage.setToken(token);
    set({ token });
  },

  localLogout: () => {
    if (get().isLoggingOut) return;
    set({ isLoggingOut: true });

    authStorage.clear();
    usePermissionStore.getState().clear();
    set({ token: null, user: null });

    // Angular reset this flag after 500ms to swallow the burst of 401s that a
    // page full of parallel requests produces. Keep the same debounce; without it
    // every in-flight request would trigger its own logout.
    setTimeout(() => set({ isLoggingOut: false }), 500);
  },

  isAuthenticated: () => {
    const token = get().token;
    return !!token && !isTokenExpired(token);
  },
}));

